import { useCallback, useState } from 'react';

import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import CardContent from '@material-ui/core/CardContent';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import Head from '../../../components/Head';
import Card from '../../../components/Auth/Card';
import ErrorMessage from '../../../components/ErrorMessage';
import authService from '../../../services/useCases/auth';
import AuthHeader from '../../../components/Auth/Header';
import BackToLogin from '../../../components/Auth/BackToLogin';
import { AtSignIcon } from '../../../assets/icons';
import useStyles from './styles';
import useAuthStyles from '../../../styles/pages/auth';

interface Values {
  email: string;
  password: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email é obrigatório')
    .email('Digite um email válido'),
});

const FormForgotPassword = () => {
  const classes = useStyles();
  const authClasses = useAuthStyles();

  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  const handleFormik = async (
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) => {
    try {
      handleClose();
      await authService.sendRecoveryPawssword(values.email);
      formikHelpers.resetForm({ values: {} as Values });
      setHasSuccess(true);
    } catch (error) {
      setHasError(true);
    }
    formikHelpers.setSubmitting(false);
  };

  const handleClose = useCallback(() => {
    setHasError(false);
    setHasSuccess(false);
  }, []);

  return (
    <Container maxWidth="sm" style={{ zIndex: 2 }}>
      <Head>Esqueci a senha</Head>
      <Formik
        initialValues={{} as Values}
        validationSchema={validationSchema}
        onSubmit={handleFormik}
      >
        {({ isSubmitting, values, handleChange, touched, errors }) => (
          <Form
            className={[authClasses.container, classes.container].join(' ')}
          >
            <Grid container className={authClasses.cardWrapper}>
              <AuthHeader outside />
              <Card className={classes.cardContainer}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={12}
                      className={authClasses.descriptionContainer}
                    >
                      <Typography variant="h1">Informe seu E-mail</Typography>
                      <Typography>
                        Vamos enviar um link em seu e-mail de cadastro, para que
                        você possa criar uma nova senha de acesso.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className={authClasses.inputsContainer}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <AtSignIcon />
                            </InputAdornment>
                          ),
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ErrorMessage
                        open={hasError || hasSuccess}
                        handleClose={handleClose}
                        title={hasError ? 'Erro' : 'Sucesso'}
                        severity={hasError ? 'error' : 'success'}
                        message={
                          hasError ? (
                            <>
                              <strong>Email</strong> não localizado
                            </>
                          ) : (
                            <>
                              Email de recuperação de senha enviado. Favor
                              chegar sua caixa de emails e clicar no link.
                            </>
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        size="large"
                        disabled={isSubmitting}
                        className={authClasses.submitButton}
                      >
                        {isSubmitting ? 'Aguarde...' : 'Recuperar senha'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <BackToLogin />
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default FormForgotPassword;
